import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, UserCog } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  useGetDriverManagerBoard,
  useGetDriverManagerHistory,
} from '../../hooks/backend/drivers'
import { ManagerCell, type ManagerState } from './ManagerCell'
import type { DriverBoardRow } from '../../utils/managers'

// The manager block on the driver detail, next to Edit driver details.
//
// It is a first-class block rather than a field inside the edit dialog on
// purpose. Every other field in that dialog is write-only, blank on open, and
// left unchanged if untouched. A manager is not like that: you need to see who
// it is now, and who changed it last, before you decide to change it.

type HistoryRow = {
  changed_at: string | null
  changed_by: string | null
  source: string | null
  reason: string | null
  from_manager: string | null
  to_manager: string | null
}

function formatWhen(value: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function DriverManagerPanel({
  applicationId,
  canEdit,
}: {
  applicationId: string
  canEdit: boolean
}) {
  const board = useGetDriverManagerBoard()
  const history = useGetDriverManagerHistory()
  const [showHistory, setShowHistory] = useState(false)

  const boardTrigger = board.trigger
  const historyTrigger = history.trigger

  const load = useCallback(() => {
    void boardTrigger({ application_id: applicationId }, { skipCache: true })
  }, [boardTrigger, applicationId])

  useEffect(() => {
    load()
  }, [load])

  const row = ((board.data as DriverBoardRow[] | undefined) ?? [])[0] ?? null

  useEffect(() => {
    if (showHistory && row?.driver_id) {
      void historyTrigger({ driver_id: row.driver_id }, { skipCache: true })
    }
  }, [showHistory, row?.driver_id, historyTrigger])

  // No driver row yet: this is still an applicant, and an applicant has no
  // manager. Render nothing rather than an empty control that invites a click
  // which would fail.
  if (!board.loading && !row) return null

  const historyRows = (history.data as HistoryRow[] | undefined) ?? []

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <UserCog className="h-3.5 w-3.5" />
        Manager
      </div>

      {board.loading && !row ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Loading
        </div>
      ) : row ? (
        <>
          <ManagerCell
            driverId={row.driver_id}
            driverName={row.driver_name || 'this driver'}
            managerPersonId={row.manager_person_id}
            managerState={(row.manager_state as ManagerState) ?? 'unassigned'}
            plate={row.plate}
            vehicleManagerPersonId={row.vehicle_manager_person_id}
            vehicleManagerName={row.vehicle_manager_name}
            canEdit={canEdit}
            // A driver holding a seat is being moved off a live coaching
            // relationship, so the move has to carry a reason. A benched driver
            // being given a manager for the first time does not.
            requireReason={row.manager_state === 'mismatch' || row.manager_state === 'ok'}
            onChanged={() => {
              load()
              if (row.driver_id) {
                void historyTrigger({ driver_id: row.driver_id }, { skipCache: true })
              }
            }}
          />

          {row.manager_state === 'not_seated' && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Not on a car this week, so no car to check against.
            </p>
          )}

          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            {showHistory ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            History
          </button>

          {showHistory && (
            <div className="mt-1.5 space-y-1.5">
              {history.loading && historyRows.length === 0 ? (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <Spinner className="h-3 w-3" /> Loading
                </div>
              ) : historyRows.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No recorded change. This manager is from the original assignment.
                </p>
              ) : (
                historyRows.map((h, i) => (
                  <div
                    key={`${h.changed_at}-${i}`}
                    className={cn('border-l-2 border-border pl-2 text-[11px]')}
                  >
                    <div className="text-foreground">
                      {h.from_manager || 'no manager'} to {h.to_manager}
                    </div>
                    <div className="text-muted-foreground">
                      {formatWhen(h.changed_at)}, {h.changed_by}
                    </div>
                    {h.reason && <div className="text-muted-foreground">{h.reason}</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}
